import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {
  BestUserPromotionPermissions,
  Choice,
  CreatePromotionTemplateInputType,
  PromotionFilterRules,
  PromotionSectionKeys,
  Section,
} from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryCampaignProvider/apis';
import { Colors, SolidColors } from '@provider-portal/common/styles/bolt-uikit-colors';
import { FONT_FAMILY } from '@provider-portal/common/styles/themeMui';
import { PromotionCreationAlert } from '@provider-portal/promotions-common/components/PromotionCreationAlert/PromotionCreationAlert';
import { MenuItemSelector } from 'promotions/components/MenuItemSelector/MenuItemSelectorFC';

import { useSelectedProvider, useSelectedProviderId } from 'hooks/useSelectedProvider';

import { SelectedMenuItem } from 'root/promotions/types';

export interface FocusPreSelection {
  [PromotionSectionKeys.TYPE_OF_DISCOUNT]?: string;
  [PromotionSectionKeys.DISCOUNT_VALUE_PERCENTAGE]?: string;
  [PromotionSectionKeys.DISCOUNT_TARGETING]?: string;
  [PromotionSectionKeys.DOUBLE_DEAL_VALUE]?: string;
}

interface Props {
  section: Section;
  sectionIndex: number;
  choiceIndex: number | null;
  onSelect: (sectionIndex: number, choiceIndex: number, choice: Choice) => void;
  bestUserPromotionsPermission: BestUserPromotionPermissions;
  showMenuItemSelector: boolean;
  setMenuItemsSelected: React.Dispatch<React.SetStateAction<SelectedMenuItem[]>>;
  setIsSelectedItemsOptionSelected: React.Dispatch<React.SetStateAction<boolean>>;
  preselectedProductIds: string[];
  exclusionTags: string[];
  exclusionMessage: string;
  focusPreSelection?: FocusPreSelection;
  bannedUntil?: number;
  hideDescription?: boolean;
}

const BEST_CUSTOMERS_CHOICE = 'Best customers';
const styles = {
  select: {
    backgroundColor: 'colors.neutral200',
    borderRadius: 2,
    width: 400,
  },
  header: {
    fontSize: '1.15em',
  },
  description: {
    color: SolidColors.neutral800,
  },
  bestUserPromotionsAlert: {
    fontFamily: FONT_FAMILY,
    fontSize: '.875rem',
    lineHeight: '1.25rem',
    fontWeight: 400,
    color: Colors.neutral900,
    borderRadius: '8px',
    backgroundColor: Colors.red100,
  },
  bestUserPromotionsAlertIcon: {
    color: Colors.red500,
  },
};

function isChoiceDisabled(c: Choice): boolean {
  if (c.type === CreatePromotionTemplateInputType.SELECT_DISABLED) {
    return true;
  }
  if (
    c.type === CreatePromotionTemplateInputType.SECTION ||
    c.type === CreatePromotionTemplateInputType.DROPDOWN
  ) {
    return c.section.choices.length === 0;
  }

  return false;
}

function doesSectionContainBestCustomersChoice(section: Section): boolean {
  if (section.input_type === CreatePromotionTemplateInputType.SELECT) {
    return section.choices.some((c) => c.value === BEST_CUSTOMERS_CHOICE);
  }
  return false;
}

export const BestUsersPromotionsAlert = () => {
  const intl = useIntl();
  const alertMessage = intl.formatMessage({
    id: 'components.wizardSection.bestUserPromotionsAlertMessage',
  });
  return (
    <Alert
      severity="info"
      icon={<WarningRoundedIcon sx={styles.bestUserPromotionsAlertIcon} />}
      sx={styles.bestUserPromotionsAlert}
    >
      {alertMessage}
    </Alert>
  );
};

const MenuItemSelectorSection = (props: {
  providerId: number;
  setMenuItemsSelected: React.Dispatch<React.SetStateAction<SelectedMenuItem[]>>;
  preselectedProductIds: string[];
  exclusionTags: string[];
  exclusionMessage: string;
}) => {
  const intl = useIntl();
  const sectionTitle = intl.formatMessage({
    id: 'components.wizardSection.menuItemSelectorSectionTitle',
  });
  const sectionDescription = intl.formatMessage({
    id: 'components.wizardSection.menuItemSelectorSectionDescription',
  });

  return (
    <Box mt={2}>
      <Box mb={1}>
        <Typography variant="h6" sx={styles.header}>
          {sectionTitle}
        </Typography>
      </Box>
      <Box mb={1} sx={styles.description}>
        {sectionDescription}
      </Box>
      <Box mt={2}>
        <MenuItemSelector
          providerId={props.providerId}
          setMenuItemsSelected={props.setMenuItemsSelected}
          preselectedProductIds={props.preselectedProductIds}
          exclusionTags={props.exclusionTags}
          exclusionMessage={props.exclusionMessage}
        />
      </Box>
    </Box>
  );
};

export function WizardSection({
  section,
  sectionIndex,
  onSelect,
  choiceIndex,
  bestUserPromotionsPermission,
  focusPreSelection,
  bannedUntil,
  showMenuItemSelector,
  setMenuItemsSelected,
  setIsSelectedItemsOptionSelected,
  preselectedProductIds,
  exclusionTags,
  exclusionMessage,
  hideDescription,
}: Props) {
  const providerId = useSelectedProviderId();
  const { menuEditorSettings: providerMenuEditorPermission } = useSelectedProvider();

  const handleSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<number>) => {
      const currentChoiceIndex = Number(e.target.value);
      if (section.key === PromotionSectionKeys.TYPE_OF_DISCOUNT) {
        if (
          section.choices[currentChoiceIndex].key ===
          PromotionFilterRules.TYPE_OF_DISCOUNT_SELECTED_ITEMS
        ) {
          setIsSelectedItemsOptionSelected(true);
        } else {
          setIsSelectedItemsOptionSelected(false);
          setMenuItemsSelected([]);
        }
      }
      if (Number.isNaN(currentChoiceIndex)) {
        throw new Error('Choice index should be number');
      }

      onSelect(sectionIndex, currentChoiceIndex, section.choices[currentChoiceIndex]);
    },
    [
      section.key,
      section.choices,
      onSelect,
      sectionIndex,
      setIsSelectedItemsOptionSelected,
      setMenuItemsSelected,
    ],
  );
  const collator = useMemo(
    () => new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' }),
    [],
  );
  const [preSelectedIndex, setPreSelectedIndex] = useState(-1);

  useEffect(() => {
    if (preSelectedIndex !== -1) {
      onSelect(sectionIndex, preSelectedIndex, section.choices[preSelectedIndex]);
    }
  }, [preSelectedIndex, sectionIndex, onSelect, section]);

  const defaultValue = useMemo(() => {
    const keyMapping: Record<string, string> = {
      [PromotionSectionKeys.TYPE_OF_DISCOUNT]:
        focusPreSelection?.[PromotionSectionKeys.TYPE_OF_DISCOUNT] ===
        'type_of_discount_double_deal'
          ? 'type_of_discount_double_deal_v2'
          : focusPreSelection?.[PromotionSectionKeys.TYPE_OF_DISCOUNT],
      [PromotionSectionKeys.DISCOUNT_TARGETING]:
        focusPreSelection?.[PromotionSectionKeys.DISCOUNT_TARGETING],
      [PromotionSectionKeys.DISCOUNT_VALUE_PERCENTAGE]:
        focusPreSelection?.[PromotionSectionKeys.DISCOUNT_VALUE_PERCENTAGE],
      [PromotionSectionKeys.DOUBLE_DEAL_VALUE]:
        focusPreSelection?.[PromotionSectionKeys.DOUBLE_DEAL_VALUE],
    };
    const sortedChoices = section.choices.sort((a, b) => collator.compare(a.value, b.value));
    const index = sortedChoices.findIndex((c) => {
      if ('key' in c) {
        return c.key === PromotionFilterRules.DISCOUNT_VALUE_PERCENTAGE
          ? c.value === keyMapping[section.key]
          : c.key === keyMapping[section.key];
      }
      return false;
    });
    setPreSelectedIndex(index);

    return index;
  }, [focusPreSelection, section, collator]);

  return (
    <Box mb={3}>
      <Box mb={1}>
        <Typography variant="h6" sx={styles.header}>
          {section.name}
        </Typography>
      </Box>
      <Box mb={1} sx={styles.description}>
        {hideDescription ? '' : section.description}
      </Box>
      {section.key === PromotionSectionKeys.TYPE_OF_DISCOUNT && !!bannedUntil && (
        <Box mb={2}>
          <PromotionCreationAlert bannedUntil={bannedUntil} />
        </Box>
      )}
      {showMenuItemSelector && providerMenuEditorPermission.is_enabled && (
        <Box mb={2}>
          <PromotionCreationAlert isSelectedItemsBan />
        </Box>
      )}
      <Box>
        {section.input_type === CreatePromotionTemplateInputType.DROPDOWN && (
          <FormControl variant="outlined" sx={styles.select}>
            <Select
              onChange={handleSelect}
              value={choiceIndex ?? defaultValue}
              defaultValue={defaultValue}
            >
              {section.choices
                .sort((a, b) => collator.compare(a.value, b.value))
                .map((c, i) => (
                  <MenuItem key={`${c.key}-${c.type}-${c.value}`} value={i}>
                    {c.value}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        )}
        {section.input_type === CreatePromotionTemplateInputType.SELECT && (
          <FormControl component="fieldset">
            <RadioGroup
              onChange={handleSelect}
              value={choiceIndex ?? defaultValue}
              defaultValue={defaultValue}
            >
              {section.choices
                .sort((a, b) => collator.compare(a.value, b.value))
                .map((c, i) => (
                  <FormControlLabel
                    key={`${c.key}-$${c.type}-${c.value}`}
                    value={i}
                    label={c.value}
                    control={<Radio name={section.key} />}
                    disabled={isChoiceDisabled(c)}
                  />
                ))}
            </RadioGroup>
          </FormControl>
        )}
      </Box>
      {section.key === PromotionSectionKeys.DISCOUNT_TARGETING &&
        bestUserPromotionsPermission.is_allowed_to_see_best_user_promotion &&
        !bestUserPromotionsPermission.is_allowed_to_create_best_user_promotion &&
        doesSectionContainBestCustomersChoice(section) && (
          <Box mb={2}>
            <BestUsersPromotionsAlert />
          </Box>
        )}
      {showMenuItemSelector && (
        <MenuItemSelectorSection
          providerId={providerId}
          setMenuItemsSelected={setMenuItemsSelected}
          preselectedProductIds={preselectedProductIds}
          exclusionTags={exclusionTags}
          exclusionMessage={exclusionMessage}
        />
      )}
    </Box>
  );
}
